// src/components/studentList/studentDetails/FeeManagementComponents/PaymentHistoryTable.js
import React, { useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableContainer,
    TableHead, TableRow, Tooltip, IconButton, TableFooter, TableCell,
    Button, Chip,
} from '@mui/material';
import { Print as PrintIcon, Payment as PaymentIcon, LocalPrintshop as PrintTableIcon } from '@mui/icons-material';
import { styled } from '@mui/system';
import { getOrdinal, formatNumber } from '../../../utils/formattingUtils';
import { format } from 'date-fns';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto', // Use auto for scrollbars only if needed
}));
const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100]
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: theme.spacing(1, 1.5),
    textAlign: 'center',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary
}));
const ContentTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: '0.85rem',
    padding: theme.spacing(1, 1.5),
    textAlign: 'center',
    verticalAlign: 'middle'
}));
const FooterTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '0.95rem',
    padding: theme.spacing(1.5, 1.5),
    textAlign: 'right'
}));
const FooterValueCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '0.95rem',
    padding: theme.spacing(1.5, 1.5),
    textAlign: 'center',
    color: theme.palette.success.dark
}));

const getSafe = (value, fallback = '-') => value !== null && value !== undefined && value !== '' ? value : fallback;

const PaymentHistoryTable = ({
    paymentHistory,
    handlePrintReceipt,
    formatCurrency,
    studentName = "Student",
    schoolDetails = { name: 'School Name', address: '', contact: '', logoUrl: '' },
    studentDetails = { admissionNo: 'N/A', classSection: 'N/A', academicYear: 'N/A' },
    feeSummaryDetails = { totalAssigned: 0, totalPaid: 0, balanceDue: 0 }
}) => {

    const formatDateSafe = useCallback((dateString, fmt = 'dd MMM yyyy') => {
        try{if(!dateString)return'N/A';const d=new Date(dateString.split('T')[0]);return isNaN(d.getTime())?'Invalid Date':format(d,fmt)}catch{return'Invalid Date'}
    }, []);

    const getPaymentModeChip = useCallback((mode) => {
        let c='default';if(!mode)return null;switch(mode.toLowerCase()){case 'cash':c='success';break;case 'upi':c='info';break;case 'card':c='warning';break;case 'cheque':c='secondary';break;case 'bank transfer':c='primary';break;default:c='default'}return <Chip label={mode} color={c} size="small" variant="outlined" icon={<PaymentIcon fontSize="small"/>} sx={{mr:-1}}/>
    }, []);

    const totalPaid = paymentHistory.reduce((sum, fee) => sum + Number(fee.amount || 0), 0);

    const handlePrintTable = useCallback(() => {
        if (!paymentHistory || paymentHistory.length === 0) {
            console.warn("No payment history to print.");
            return;
        }

        const tableRowsHtml = paymentHistory
            .sort((a, b) => new Date(a.paymentDate) - new Date(b.paymentDate))
            .map((fee, index) => `
            <tr class="payment-row">
                <td>${index + 1}</td>
                <td>${formatDateSafe(fee.paymentDate)}</td>
                <td>${getSafe(fee.paymentMode)}</td>
                <td class="amount">${formatCurrency ? formatCurrency(fee.amount) : `₹${formatNumber(fee.amount)}`}</td>
                <td>${getSafe(fee.referenceNumber)}</td>
                <td class="comments">${getSafe(fee.comments)}</td>
            </tr>
        `).join('');

        const totalFeeAssignedFormatted = formatCurrency ? formatCurrency(feeSummaryDetails.totalAssigned) : `₹${formatNumber(feeSummaryDetails.totalAssigned)}`;
        const totalPaidFormatted = formatCurrency ? formatCurrency(feeSummaryDetails.totalPaid) : `₹${formatNumber(feeSummaryDetails.totalPaid)}`;
        const pendingFeeFormatted = formatCurrency ? formatCurrency(feeSummaryDetails.balanceDue) : `₹${formatNumber(feeSummaryDetails.balanceDue)}`;
        const historyTotalPaidFormatted = formatCurrency ? formatCurrency(totalPaid) : `₹${formatNumber(totalPaid)}`;

        const printHtml = `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <title>Payment History Report - ${studentName}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
                <style>
                    @page { size: A4 portrait; margin: 15mm; }
                    body { font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif; font-size: 9.5pt; line-height: 1.4; color: #333; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .report-print-container { width: 100%; margin: 0 auto; box-sizing: border-box; display: flex; flex-direction: column; }
                    .report-content { padding: 0; flex-grow: 1; display: flex; flex-direction: column; }
                    .report-header, .report-title, .student-info-section, .payment-history-table-section, .summary-section, .report-footer-section { break-inside: avoid; page-break-inside: avoid; margin-bottom: 5mm; }
                    .report-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1.5px solid #4a90e2; padding-bottom: 3mm; }
                    .school-info { flex-grow: 1; padding-right: 5mm; }
                    .school-info h1 { margin: 0 0 1mm 0; font-size: 14pt; color: #004085; font-weight: 600; }
                    .school-info p { margin: 0.5mm 0; font-size: 9pt; color: #444; line-height: 1.3; }
                    .school-logo { flex-shrink: 0; text-align: right; }
                    .school-logo img { max-height: 45px; max-width: 150px; object-fit: contain; vertical-align: middle; }
                    .school-logo span { display: inline-block; width: 45px; height: 45px; line-height: 45px; text-align: center; background-color: #004085; color: white; border-radius: 50%; font-size: 18pt; font-weight: 600; }
                    .report-title { text-align: center; margin-bottom: 5mm; }
                    .report-title h2 { margin: 0; font-size: 13pt; color: #047857; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
                    .student-info-section table { width: 100%; border-collapse: collapse; font-size: 9.5pt; margin-bottom: 5mm; background-color: #fdfdfd; border: 1px solid #eee; border-radius: 4px; padding: 2mm;}
                    .student-info-section td { padding: 1.5mm 2mm; vertical-align: top; }
                    .student-info-section .label { font-weight: 500; color: #555; width: 20%; white-space: nowrap; }
                    .student-info-section .value { font-weight: 600; color: #111; width: 30%; }
                    .payment-history-table-section h3 { font-size: 11pt; color: #0d47a1; border-bottom: 1px solid #b3e5fc; padding-bottom: 1.5mm; margin-bottom: 3mm; }
                    .payment-history-table-section table { width: 100%; border-collapse: collapse; border: 1px solid #e0e0e0; }
                    .payment-history-table-section th, .payment-history-table-section td { border: 1px solid #f0f0f0; padding: 2mm; text-align: left; font-size: 9pt; vertical-align: middle; }
                    .payment-history-table-section thead th { background-color: #f5faff; color: #0d47a1; font-weight: 600; text-align: center; }
                    .payment-history-table-section tbody tr:nth-child(even) { background-color: #fcfdff; }
                    .payment-history-table-section td { text-align: center; }
                    .payment-history-table-section td:first-child { text-align: center; }
                    .payment-history-table-section td.amount { text-align: right; font-weight: 500; }
                    .payment-history-table-section td.comments { text-align: left; max-width: 220px; white-space: normal; word-break: break-word; font-size: 8.5pt; }
                    .payment-history-table-section tfoot td { border: none; border-top: 1.5px solid #aaa; font-weight: bold; padding-top: 3mm; }
                    .payment-history-table-section tfoot td.amount { text-align: right; }
                    .summary-section { background-color: #f8f9fa; padding: 4mm; border: 1px solid #e9ecef; border-radius: 4px; margin-top: 6mm; }
                    .summary-section h3 { font-size: 11pt; color: #0d47a1; margin-top: 0; margin-bottom: 3mm; text-align: center; }
                    .summary-section table { width: 70%; margin: 0 auto; font-size: 10pt; border-collapse: collapse;}
                    .summary-section td { padding: 2mm 3mm; border-bottom: 1px dotted #ddd; }
                    .summary-section tr:last-child td { border-bottom: none; }
                    .summary-section .label { text-align: right; font-weight: 500; color: #555; width: 60%; padding-right: 4mm;}
                    .summary-section .value { font-weight: 600; color: #212529; width: 40%; text-align: right; }
                    .summary-section .pending { color: #c82333; font-weight: 700; }
                    .report-footer-section { margin-top: auto; padding-top: 5mm; border-top: 1px solid #ccc; text-align: center; font-size: 8.5pt; color: #666; flex-shrink: 0; }
                </style>
            </head>
            <body>
                <div class="report-print-container">
                    <div class="report-content">
                        <header class="report-header">
                             <div class="school-info">
                                <h1>${getSafe(schoolDetails.name, 'School Name')}</h1>
                                <p>${getSafe(schoolDetails.address, '')}</p>
                                <p>${getSafe(schoolDetails.contact, '')}</p>
                            </div>
                            ${schoolDetails.logoUrl ? `<div class="school-logo"><img src="${schoolDetails.logoUrl}" alt="Logo"></div>` : `<div class="school-logo"><span>${getSafe(schoolDetails.name, 'S').charAt(0)}</span></div>`}
                        </header>

                        <section class="report-title"><h2>Student Payment History Report</h2></section>

                        <section class="student-info-section">
                             <table>
                                <tr>
                                    <td class="label">Student Name:</td><td class="value">${studentName}</td>
                                    <td class="label">Admission No:</td><td class="value">${getSafe(studentDetails.admissionNo)}</td>
                                </tr>
                                <tr>
                                    <td class="label">Class / Section:</td><td class="value">${getSafe(studentDetails.classSection)}</td>
                                    <td class="label">Academic Year:</td><td class="value">${getSafe(studentDetails.academicYear)}</td>
                                </tr>
                            </table>
                        </section>

                        <section class="payment-history-table-section">
                            <h3>Payment Details</h3>
                            <table>
                                <thead>
                                    <tr><th>#</th><th>Date</th><th>Mode</th><th style="text-align: right;">Amount Paid</th><th>Reference</th><th>Comments</th></tr>
                                </thead>
                                <tbody>${tableRowsHtml}</tbody>
                                 <tfoot>
                                    <tr>
                                        <td colspan="3" style="text-align: right;">Total Paid (History):</td>
                                        <td class="amount">${historyTotalPaidFormatted}</td>
                                        <td colspan="2"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </section>

                       <section class="summary-section">
                            <h3>Overall Fee Summary</h3>
                            <table>
                                <tr><td class="label">Total Assigned Fee:</td><td class="value">${totalFeeAssignedFormatted}</td></tr>
                                <tr><td class="label">Total Amount Paid:</td><td class="value">${totalPaidFormatted}</td></tr>
                                <tr><td class="label">Balance Due:</td><td class="value pending">${pendingFeeFormatted}</td></tr>
                            </table>
                       </section>

                       <section class="report-footer-section">Printed on: ${format(new Date(), 'dd MMM yyyy, hh:mm a')}</section>
                    </div>
                </div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank', 'height=800,width=1100');
        if (printWindow) {
            printWindow.document.write(printHtml);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => { try { printWindow.print(); } catch (e) { console.error("Print failed:", e); } }, 500);
        } else {
            console.error('Could not open print window. Check popup blocker.');
        }

    }, [
        paymentHistory, studentName, schoolDetails, studentDetails, feeSummaryDetails,
        formatCurrency, formatDateSafe, totalPaid, formatNumber
    ]);


    if (!paymentHistory || paymentHistory.length === 0) {
         return (
             <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: theme => `1px dashed ${theme.palette.divider}`, mt: 2 }}>
                 <Typography variant="body1" color="textSecondary">
                     No payment history available.
                 </Typography>
             </Paper>
         );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Tooltip title="Print Detailed Payment History">
                     <span>
                        <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            startIcon={<PrintTableIcon />}
                            onClick={handlePrintTable}
                            disabled={!paymentHistory || paymentHistory.length === 0}
                        >
                            Print History
                        </Button>
                     </span>
                </Tooltip>
            </Box>

            <StyledTableContainer component={Paper}>
                <Table aria-label="payment history table" size="small">
                    <StyledTableHead>
                        <TableRow>
                            <StyledTableCell align="left" sx={{ pl: 2 }}>#</StyledTableCell>
                            <StyledTableCell>Date</StyledTableCell>
                            <StyledTableCell>Mode</StyledTableCell>
                            <StyledTableCell align="right">Amount</StyledTableCell>
                            <StyledTableCell>Reference</StyledTableCell>
                            <StyledTableCell align="left">Comments</StyledTableCell>
                            <StyledTableCell align="center">Receipt</StyledTableCell>
                        </TableRow>
                    </StyledTableHead>
                    <TableBody>
                        {paymentHistory.map((fee, index) => (
                            <TableRow key={fee.feeId || index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <ContentTableCell align="left" sx={{ pl: 2 }}>{index + 1}</ContentTableCell>
                                <ContentTableCell>{formatDateSafe(fee.paymentDate)}</ContentTableCell>
                                <ContentTableCell>{getPaymentModeChip(fee.paymentMode)}</ContentTableCell>
                                <ContentTableCell align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                                    {formatCurrency ? formatCurrency(fee.amount) : `₹${formatNumber(fee.amount)}`}
                                </ContentTableCell>
                                <ContentTableCell>{getSafe(fee.referenceNumber)}</ContentTableCell>
                                <ContentTableCell align="left" sx={{ maxWidth: '180px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    <Tooltip title={getSafe(fee.comments)} arrow placement="top">
                                        <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '3.6em', lineHeight: '1.2em' }}>
                                            {getSafe(fee.comments)}
                                        </Box>
                                    </Tooltip>
                                </ContentTableCell>
                                <ContentTableCell align="center">
                                    <Tooltip title="Print Individual Receipt (A5)">
                                        <IconButton color="primary" size="small" onClick={() => handlePrintReceipt(fee)}>
                                            <PrintIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </ContentTableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter sx={{ backgroundColor: theme => theme.palette.grey[100] }}>
                        <TableRow>
                            <FooterTableCell colSpan={3} align="right">
                                Total Paid:
                            </FooterTableCell>
                            <FooterValueCell align="right">
                                {formatCurrency ? formatCurrency(totalPaid) : `₹${formatNumber(totalPaid)}`}
                            </FooterValueCell>
                            <FooterTableCell colSpan={3}></FooterTableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </StyledTableContainer>
        </Box>
    );
};

export default PaymentHistoryTable;
